import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Grid
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { roleApplicationService } from '../../services/roleApplicationService';
import { hasAnyRole } from '../../utils/auth';

const RoleApplications = ({ currentUser }) => {
  console.log('RoleApplications component loaded with currentUser:', currentUser);
  console.log('RoleApplications - currentUser.role:', currentUser?.role);
  console.log('RoleApplications - currentUser.roleIds:', currentUser?.roleIds);
  
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewApproved, setReviewApproved] = useState(true);

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      let data = [];
      
      if (selectedTab === 0) {
        // All applications
        data = await roleApplicationService.getAllApplications();
      } else if (selectedTab === 1) {
        // Pending applications
        const allData = await roleApplicationService.getAllApplications();
        data = allData.filter(app => app.status === 'PENDING');
      } else if (selectedTab === 2) {
        // Approved applications
        const allData = await roleApplicationService.getAllApplications();
        data = allData.filter(app => app.status === 'APPROVED');
      } else if (selectedTab === 3) {
        // Rejected applications
        const allData = await roleApplicationService.getAllApplications();
        data = allData.filter(app => app.status === 'REJECTED');
      }
      
      setApplications(data);
      setError(null);
    } catch (err) {
      setError('Failed to load applications: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedTab]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const handleReview = (application) => {
    setSelectedApplication(application);
    setReviewNotes('');
    setReviewApproved(true);
    setReviewDialogOpen(true);
  };

  const handleReviewSubmit = async () => {
    try {
      // For now, using a mock admin user ID - in a real app, this would come from authentication
      const adminUserId = currentUser?.id || 1;
      
      if (reviewApproved) {
        await roleApplicationService.approveApplication(
          selectedApplication.id,
          adminUserId,
          reviewNotes
        );
      } else {
        await roleApplicationService.rejectApplication(
          selectedApplication.id,
          adminUserId,
          reviewNotes
        );
      }
      
      setReviewDialogOpen(false);
      setSelectedApplication(null);
      setReviewNotes('');
      loadApplications();
    } catch (err) {
      setError('Failed to submit review: ' + err.message);
    }
  };

  const handleTeamSupervisorStatusUpdate = async (applicationId, field, value) => {
    try {
      const statusUpdates = { [field]: value };
      await roleApplicationService.updateTeamSupervisorStatus(applicationId, statusUpdates);
      loadApplications();
    } catch (err) {
      setError('Failed to update status: ' + err.message);
    }
  };

  const getStatusChip = (application) => {
    if (application.status === 'APPROVED') {
      return <Chip label="Approved" color="success" size="small" />;
    } else if (application.status === 'REJECTED') {
      return <Chip label="Rejected" color="error" size="small" />;
    } else {
      return <Chip label="Pending" color="warning" size="small" />;
    }
  };

  const getTeamSupervisorStatusChips = (application) => {
    if (application.roleName !== 'Team Supervisor') {
      return null;
    }

    return (
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
        <Chip 
          label="Admin Approved" 
          color={application.adminApproved ? "success" : "default"} 
          size="small"
          onClick={() => handleTeamSupervisorStatusUpdate(application.id, 'adminApproved', !application.adminApproved)}
          sx={{ cursor: 'pointer' }}
        />
        <Chip 
          label="BC Received" 
          color={application.backgroundCheckReceived ? "success" : "default"} 
          size="small"
          onClick={() => handleTeamSupervisorStatusUpdate(application.id, 'backgroundCheckReceived', !application.backgroundCheckReceived)}
          sx={{ cursor: 'pointer' }}
        />
        <Chip 
          label="BC Approved" 
          color={application.backgroundCheckApproved ? "success" : "default"} 
          size="small"
          onClick={() => handleTeamSupervisorStatusUpdate(application.id, 'backgroundCheckApproved', !application.backgroundCheckApproved)}
          sx={{ cursor: 'pointer' }}
        />
        {application.ichatReceived && (
          <Chip 
            label="ICHAT Received" 
            color={application.ichatReceived ? "success" : "default"} 
            size="small"
            onClick={() => handleTeamSupervisorStatusUpdate(application.id, 'ichatReceived', !application.ichatReceived)}
            sx={{ cursor: 'pointer' }}
          />
        )}
        {application.ichatApproved && (
          <Chip 
            label="ICHAT Approved" 
            color={application.ichatApproved ? "success" : "default"} 
            size="small"
            onClick={() => handleTeamSupervisorStatusUpdate(application.id, 'ichatApproved', !application.ichatApproved)}
            sx={{ cursor: 'pointer' }}
          />
        )}
      </Box>
    );
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return 'warning';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'PENDING':
        return 'Pending Review';
      case 'APPROVED':
        return 'Approved';
      case 'REJECTED':
        return 'Rejected';
      default:
        return status;
    }
  };

  const canReview = (application) => {
    if (!currentUser || !application) {
      return false;
    }
    
    // Get user role from currentUser prop (same as Navigation component)
    const userRole = currentUser?.role || 'user';
    console.log('canReview - currentUser:', currentUser);
    console.log('canReview - userRole:', userRole);
    console.log('canReview - checking roles:', ['staff', 'coach']);
    console.log('canReview - application status:', application.status);
    
    // Only allow review if application is pending and user has appropriate role
    const canReview = ['staff', 'coach'].includes(userRole) && application.status === 'PENDING';
    console.log('canReview - result:', canReview);
    return canReview;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Handle case where currentUser is null
  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">User not authenticated. Please log in again.</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom color="primary">
        Role Applications
      </Typography>
      
      <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="All Applications" />
        <Tab label="Pending Review" />
        <Tab label="Approved" />
        <Tab label="Rejected" />
      </Tabs>

      {applications.length === 0 ? (
        <Alert severity="info">
          No applications found for the selected tab.
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Household</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Team Supervisor Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {applications.map((application) => (
                <TableRow key={application.id}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {application.userName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {application.userEmail}
                      </Typography>
                      {application.caregiverName && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Applying for: {application.caregiverName}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{application.roleName}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{application.householdName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {application.householdCode}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, wordBreak: 'break-word' }}>
                      {application.notes || 'No notes'}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(application)}</TableCell>
                  <TableCell>{getTeamSupervisorStatusChips(application)}</TableCell>
                  <TableCell>
                    {new Date(application.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleReview(application)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {canReview(application) && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => {
                                setSelectedApplication(application);
                                setReviewApproved(true);
                                setReviewNotes('');
                                setReviewDialogOpen(true);
                              }}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => {
                                setSelectedApplication(application);
                                setReviewApproved(false);
                                setReviewNotes('');
                                setReviewDialogOpen(true);
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onClose={() => setReviewDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Review Application - {selectedApplication?.userDisplayName || selectedApplication?.userName}
        </DialogTitle>
        <DialogContent>
          {selectedApplication && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Applicant</Typography>
                  <Typography variant="body2">
                    {selectedApplication.userDisplayName || selectedApplication.userName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedApplication.userEmail}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Role</Typography>
                  <Typography variant="body2">
                    {selectedApplication.roleName}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Household</Typography>
                  <Typography variant="body2">
                    {selectedApplication.householdName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedApplication.householdCode}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Status</Typography>
                  <Chip 
                    label={getStatusLabel(selectedApplication.status)} 
                    color={getStatusColor(selectedApplication.status)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2">Reason</Typography>
                  <Typography variant="body2">
                    {selectedApplication.reason || 'No reason provided'}
                  </Typography>
                </Grid>
                {selectedApplication.adminNotes && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Admin Notes</Typography>
                    <Typography variant="body2">
                      {selectedApplication.adminNotes}
                    </Typography>
                  </Grid>
                )}
                {selectedApplication.reviewedByName && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Reviewed By</Typography>
                    <Typography variant="body2">
                      {selectedApplication.reviewedByName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(selectedApplication.reviewedAt)}
                    </Typography>
                  </Grid>
                )}
                {selectedApplication && canReview(selectedApplication) && (
                  <>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Decision</InputLabel>
                        <Select
                          value={reviewApproved}
                          onChange={(e) => setReviewApproved(e.target.value)}
                          label="Decision"
                        >
                          <MenuItem value={true}>Approve</MenuItem>
                          <MenuItem value={false}>Reject</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Notes"
                        value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)}
                        placeholder="Add any notes about your decision..."
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewDialogOpen(false)}>Cancel</Button>
          {selectedApplication && canReview(selectedApplication) && (
            <Button 
              onClick={handleReviewSubmit} 
              variant="contained"
              color={reviewApproved ? 'success' : 'error'}
            >
              {reviewApproved ? 'Approve' : 'Reject'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleApplications; 